import React, { useState, useEffect, useCallback, memo, useRef, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Skeleton,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  Download,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  PlayArrow,
} from "@mui/icons-material";
import {
  listFolderContents,
  getThumbnails,
  checkWebPSupport,
  getOrCreateSharedLink,
} from "../../DropboxFunctions";
import { sortImagesByFilename } from "../../ClientSelects/ImageGrid";
import { convertToDownloadUrl } from "../utils/dropboxHelpers";
import StoryboardLightbox from "./StoryboardLightbox";
import { useAtom } from "jotai";
import { themeAtom } from "../../-Atoms";
import { motion } from "framer-motion";

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|bmp|tiff?|webp|heic|heif)$/i;
const VIDEO_EXTENSIONS = /\.(mp4|mov|m4v|webm|avi|mts|m2ts)$/i;

const isImageFile = (entry) =>
  Boolean(entry?.name && IMAGE_EXTENSIONS.test(entry.name));
const isVideoFile = (entry) =>
  Boolean(entry?.name && VIDEO_EXTENSIONS.test(entry.name));

const FolderHeader = memo(function FolderHeader({
  name,
  downloadUrl,
  onDownload,
  compact = false,
  fileCount = null,
  selectsMade = false,
  selectedCount = 0,
  onEditSelects,
  selectsSetup = false,
}) {
  const [theme] = useAtom(themeAtom);
  const isSmallScreen = useMediaQuery("(max-width:1200px)");
  const isDarkMode = theme === "dark";
  const headingColor = isDarkMode ? "#f8fafc" : "#1f2434";
  const secondaryColor = isDarkMode
    ? "rgba(248, 250, 252, 0.75)"
    : "rgba(31, 36, 48, 0.7)";

  const statusInfo = selectsMade
    ? {
      background: "#8B5CF6",
      hover: "#7C3AED",
      icon: <VisibilityIcon fontSize="small" />,
      label: "View Selects",
    }
    : {
      background: "#D97706",
      hover: "#B45309",
      icon: <EditIcon fontSize="small" />,
      label: "Edit Selects",
    };

  const actionButtonBase = {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 1,
    px: 2,
    py: 0.65,
    fontSize: isSmallScreen ? "0.75rem" : "0.85rem",
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    minHeight: "34px",
    boxShadow: "none",
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: compact ? 0 : 2,
        pb: compact ? 0 : 2,
        borderBottom: compact
          ? "none"
          : `1px solid ${isDarkMode
            ? "rgba(144, 202, 249, 0.18)"
            : "rgba(21, 101, 192, 0.1)"
          }`,
      }}
    >
      <Box>
        <Typography
          variant={compact ? "h6" : "h5"}
          component="h2"
          sx={{
            fontWeight: 600,
            color: headingColor,
            mb: 0.5,
            fontSize: isSmallScreen
              ? compact
                ? "0.95rem"
                : "1.05rem"
              : undefined,
          }}
        >
          {name}
        </Typography>
        {fileCount !== null ? (
          <Typography
            variant="body2"
            sx={{
              color: secondaryColor,
            }}
          >
            {fileCount} file{fileCount !== 1 ? "s" : ""}
          </Typography>
        ) : (
          <Skeleton variant="text" width={60} height={20} />
        )}
      </Box>

      {downloadUrl ? (
        <Box
          sx={{
            display: "flex",
            gap: 2,
          }}
        >
          {name === "Storyboards" && selectsSetup && (
            <Button
              onClick={onEditSelects}
              sx={{
                ...actionButtonBase,
                backgroundColor: statusInfo.background,
                color: "#fff",
                "&:hover": {
                  backgroundColor: statusInfo.hover,
                },
              }}
            >
              {statusInfo.icon}
              {isSmallScreen ? "" : `${statusInfo.label} (${selectedCount})`}
            </Button>
          )}

          <Button
            variant="contained"
            component="a"
            href={convertToDownloadUrl(downloadUrl)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              ...actionButtonBase,
              backgroundColor: "#66bb6a",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#4caf50",
              },
            }}
          >
            <Download fontSize="small" />
            {isSmallScreen ? "" : "Download"}
          </Button>

          <Button
            variant="contained"
            component="a"
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              ...actionButtonBase,
              backgroundColor: "#0061FE",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#0052d4",
              },
            }}
          >
            <img
              src="/Dropbox_Icon.png"
              alt="Dropbox"
              style={{ height: 18, width: "auto", objectFit: "contain" }}
            />
            {isSmallScreen ? "" : "Dropbox"}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
});

// eslint-disable-next-line no-unused-vars
const ImageFlexGrid = memo(function ImageFlexGrid({
  loading,
  images,
  thumbnails,
  onImageClick,
}) {
  const [theme] = useAtom(themeAtom);
  const isSmallScreen = useMediaQuery("(max-width:1200px)");
  const imageSize = isSmallScreen ? 130 : 180;
  if (loading) {
    return (
      <Box sx={{ p: 1, textAlign: "center" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "flex-start",
            mb: 2,
          }}
        >
          {[...Array(8)].map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width={imageSize}
              height={imageSize}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  if (images.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No files found in this folder.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        p: 1,
        justifyContent: "flex-start",
        overflowY: "auto",
        flex: 1,
      }}
    >
      {images.map((image, index) => {
        const url = thumbnails[image.path_lower];
        return (
          <Box
            key={image.path_lower}
            onClick={() => onImageClick && onImageClick(index)}
            sx={{
              width: imageSize,
              height: imageSize,
              overflow: "hidden",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {url ? (
              <img
                src={url}
                alt={image.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  zIndex: 1,
                  position: "relative",
                }}
                loading="lazy"
                draggable={false}
              />
            ) : (
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                sx={{
                  bgcolor:
                    theme === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)",
                  borderRadius: 2,
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
});

const MEDIA_PREVIEW_SKELETON_COUNT = 15;

const MediaPreviewRail = memo(function MediaPreviewRail({
  mediaItems = [],
  thumbnails = {},
  onSelect,
  loading = false,
  hasMore = false,
  onLoadMore,
  onLoadAll,
  loadingMore = false,
}) {
  const [theme] = useAtom(themeAtom);
  const isDarkMode = theme === "dark";

  if (!mediaItems.length && !loading) return null;

  const previewItems = mediaItems.map((entry, mediaIndex) => ({
    entry,
    mediaIndex,
  }));
  const skeletonItems = Array.from({ length: MEDIA_PREVIEW_SKELETON_COUNT });
  const renderItems = loading ? skeletonItems : previewItems;

  const previewStyles = {
    width: 92,
    height: 92,
    borderRadius: 2,
    overflow: "hidden",
    position: "relative",
    cursor: "pointer",
    border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"
      }`,
    backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "#f6f6f7",
    transition: "transform 0.15s ease, border 0.15s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.25)" : "rgba(15,23,42,0.2)"
        }`,
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {renderItems.map((item, idx) => {
          if (loading) {
            return (
              <Skeleton
                key={`preview-skeleton-${idx}`}
                variant="rectangular"
                width={92}
                height={92}
                sx={{ borderRadius: 2 }}
              />
            );
          }

          const { entry, mediaIndex } = item;
          const thumb = thumbnails[entry.path_lower];
          const isVideo = isVideoFile(entry);
          const key = entry.path_lower || `media-${mediaIndex}-${idx}`;

          return (
            <Tooltip key={key} title={entry.name}>
              <Box sx={previewStyles} onClick={() => onSelect?.(mediaIndex)}>
                {thumb ? (
                  <img
                    src={thumb}
                    alt={entry.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    loading="lazy"
                  />
                ) : (
                  <Skeleton variant="rectangular" width="100%" height="100%" />
                )}

                {isVideo && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.55))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PlayArrow sx={{ color: "white" }} />
                  </Box>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      {(loading || (hasMore && onLoadMore)) && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            mt: 1,
            minHeight: 40,
            alignItems: "center",
          }}
        >
          {loading ? (
            <Skeleton variant="rounded" width={140} height={34} />
          ) : (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={onLoadMore}
                disabled={loadingMore}
                disableRipple
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1,
                  px: 2.5,
                  py: 0.6,
                  fontSize: "0.82rem",
                  color: isDarkMode ? "#e3e7ef" : "#111827",
                  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)"
                    }`,
                  backgroundColor: isDarkMode
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(15,23,42,0.03)",
                  letterSpacing: "0.01em",
                  "&:hover": {
                    borderColor: isDarkMode
                      ? "rgba(255,255,255,0.24)"
                      : "rgba(15,23,42,0.24)",
                    backgroundColor: isDarkMode
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(15,23,42,0.08)",
                  },
                }}
              >
                {loadingMore ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Loading...
                  </>
                ) : (
                  "Load more"
                )}
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={onLoadAll}
                disabled={loadingMore}
                disableRipple
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1,
                  px: 2.5,
                  py: 0.6,
                  fontSize: "0.82rem",
                  color: isDarkMode ? "#e3e7ef" : "#111827",
                  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)"
                    }`,
                  backgroundColor: isDarkMode
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(15,23,42,0.03)",
                  letterSpacing: "0.01em",
                  "&:hover": {
                    borderColor: isDarkMode
                      ? "rgba(255,255,255,0.24)"
                      : "rgba(15,23,42,0.24)",
                    backgroundColor: isDarkMode
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(15,23,42,0.08)",
                  },
                }}
              >
                Load all
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
});

const PREVIEW_BATCH_SIZE = 15;
const LOAD_MORE_BATCH_SIZE = 30;

const FolderCollection = ({
  folder,
  onLoadingComplete,
  isActive = true,
  isCompleted = false,
  compact = false,
  selectsMade = false,
  selectedCount = 0,
  onEditSelects,
  selectsSetup = false,
}) => {
  const [theme] = useAtom(themeAtom);
  const isSmallScreen = useMediaQuery("(max-width:1200px)");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [fileCount, setFileCount] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [previewLimit, setPreviewLimit] = useState(PREVIEW_BATCH_SIZE);
  const [isLoadingMorePreviews, setIsLoadingMorePreviews] = useState(false);
  const thumbnailMimeTypeRef = useRef(null);
  const supplyBatchInFlightRef = useRef(false);

  const isStoryboards = folder?.name === "Storyboards";
  const canPreviewMedia = isStoryboards || folder?.isSupplySubfolder;
  const handleMediaOpen = useCallback(
    (index = 0) => {
      if (!mediaItems.length) return;
      const safeIndex = Math.min(Math.max(index, 0), mediaItems.length - 1);
      setLightboxIndex(safeIndex);
      setLightboxOpen(true);
    },
    [mediaItems.length]
  );

  const handleLightboxClose = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const ensureThumbnailMimeType = useCallback(async () => {
    if (thumbnailMimeTypeRef.current) {
      return thumbnailMimeTypeRef.current;
    }
    const supportsWebP = await checkWebPSupport();
    thumbnailMimeTypeRef.current = supportsWebP
      ? "data:image/webp;base64,"
      : "data:image/jpeg;base64,";
    return thumbnailMimeTypeRef.current;
  }, []);

  const fetchNextSupplyBatch = useCallback(async () => {
    if (!folder?.isSupplySubfolder) return;
    if (!mediaItems.length) return;
    if (previewLimit >= mediaItems.length) return;
    if (supplyBatchInFlightRef.current) return;

    supplyBatchInFlightRef.current = true;
    setIsLoadingMorePreviews(true);

    try {
      const startIndex = previewLimit;
      // Use smaller batch size for initial load (when previewLimit is 0), larger for subsequent loads
      const batchSize = previewLimit === 0 ? PREVIEW_BATCH_SIZE : LOAD_MORE_BATCH_SIZE;
      const endIndex = Math.min(
        previewLimit + batchSize,
        mediaItems.length
      );
      const batchItems = mediaItems.slice(startIndex, endIndex);
      const thumbnailTargets = batchItems.filter(
        (entry) => isImageFile(entry) || isVideoFile(entry)
      );

      if (thumbnailTargets.length > 0) {
        const mimeType = await ensureThumbnailMimeType();
        await getThumbnails(thumbnailTargets, {
          size: isStoryboards ? "w2048h1536" : "w1024h768",
          mode: "strict",
          onBatch: (batchThumbs) => {
            const prefixed = {};
            Object.entries(batchThumbs).forEach(([pathLower, base64]) => {
              prefixed[pathLower] = mimeType + base64;
            });
            setThumbnails((prev) => ({ ...prev, ...prefixed }));
          },
        });
      }

      setPreviewLimit(endIndex);
    } catch (error) {
      console.error(
        `Failed to load thumbnails for ${folder?.name || "folder"}:`,
        error
      );
    } finally {
      supplyBatchInFlightRef.current = false;
      setIsLoadingMorePreviews(false);
    }
  }, [
    folder?.isSupplySubfolder,
    folder?.name,
    mediaItems,
    previewLimit,
    ensureThumbnailMimeType,
    isStoryboards,
  ]);

  const visibleMediaItems = mediaItems.slice(0, previewLimit);
  const hasMoreMedia = mediaItems.length > previewLimit;
  const supplyAwaitingFirstBatch =
    folder?.isSupplySubfolder && previewLimit === 0 && mediaItems.length > 0;
  const previewRailLoading = loading || supplyAwaitingFirstBatch;

  const handleLoadMoreMedia = useCallback(() => {
    if (folder?.isSupplySubfolder) {
      fetchNextSupplyBatch();
      return;
    }
    setPreviewLimit((prev) => {
      if (!mediaItems.length) {
        return prev;
      }
      return Math.min(prev + LOAD_MORE_BATCH_SIZE, mediaItems.length);
    });
  }, [folder?.isSupplySubfolder, mediaItems.length, fetchNextSupplyBatch]);

  const handleLoadAllMedia = useCallback(() => {
    if (!mediaItems.length) return;

    if (folder?.isSupplySubfolder) {
      // For supply folders, load all remaining items at once
      const remainingCount = mediaItems.length - previewLimit;
      if (remainingCount <= 0) return;

      // Set preview limit to total to trigger loading all
      setPreviewLimit(mediaItems.length);

      // Manually trigger thumbnail loading for all remaining items
      (async () => {
        if (supplyBatchInFlightRef.current) return;
        supplyBatchInFlightRef.current = true;
        setIsLoadingMorePreviews(true);

        try {
          const startIndex = previewLimit;
          const batchItems = mediaItems.slice(startIndex);
          const thumbnailTargets = batchItems.filter(
            (entry) => isImageFile(entry) || isVideoFile(entry)
          );

          if (thumbnailTargets.length > 0) {
            const mimeType = await ensureThumbnailMimeType();
            await getThumbnails(thumbnailTargets, {
              size: isStoryboards ? "w2048h1536" : "w1024h768",
              mode: "strict",
              onBatch: (batchThumbs) => {
                const prefixed = {};
                Object.entries(batchThumbs).forEach(([pathLower, base64]) => {
                  prefixed[pathLower] = mimeType + base64;
                });
                setThumbnails((prev) => ({ ...prev, ...prefixed }));
              },
            });
          }
        } catch (error) {
          console.error(
            `Failed to load all thumbnails for ${folder?.name || "folder"}:`,
            error
          );
        } finally {
          supplyBatchInFlightRef.current = false;
          setIsLoadingMorePreviews(false);
        }
      })();
    } else {
      // For regular folders, just set limit to all
      setPreviewLimit(mediaItems.length);
    }
  }, [folder?.isSupplySubfolder, folder?.name, mediaItems, previewLimit, isStoryboards, ensureThumbnailMimeType]);

  const handleLightboxRequestMore = useCallback(
    (requestedIndex) => {
      // Only auto-load if the requested index is beyond current preview limit
      if (requestedIndex < previewLimit) return;

      // Don't trigger if already loading
      if (isLoadingMorePreviews) return;

      // Trigger load more
      handleLoadMoreMedia();
    },
    [previewLimit, isLoadingMorePreviews, handleLoadMoreMedia]
  );

  useEffect(() => {
    setPreviewLimit(folder?.isSupplySubfolder ? 0 : PREVIEW_BATCH_SIZE);
    setIsLoadingMorePreviews(false);
    supplyBatchInFlightRef.current = false;
  }, [folder?.path, folder?.isSupplySubfolder]);

  useEffect(() => {
    if (folder?.isSupplySubfolder) return;
    setPreviewLimit((prev) =>
      Math.min(
        Math.max(PREVIEW_BATCH_SIZE, prev),
        mediaItems.length || PREVIEW_BATCH_SIZE
      )
    );
  }, [mediaItems.length, folder?.isSupplySubfolder]);

  useEffect(() => {
    if (!folder?.isSupplySubfolder) return;
    if (!mediaItems.length) return;
    if (previewLimit > 0) return;
    if (isLoadingMorePreviews) return;
    fetchNextSupplyBatch();
  }, [
    folder?.isSupplySubfolder,
    mediaItems.length,
    previewLimit,
    fetchNextSupplyBatch,
    isLoadingMorePreviews,
  ]);

  useEffect(() => {
    let isCancelled = false;

    const loadFolderData = async () => {
      // Reset state for new load
      setImages([]);
      setMediaItems([]);
      setThumbnails({});
      setError(null);
      setLoading(true);

      let entries = [];
      try {
        const result = await listFolderContents(folder.path);
        if (isCancelled) return;

        entries = result.entries || [];
        setFileCount(entries.length);

        if (entries.length > 0) {
          const sortedEntries = entries.slice().sort(sortImagesByFilename);

          if (canPreviewMedia) {
            const imageFiles = sortedEntries.filter(isImageFile);
            const videoFiles = sortedEntries.filter(isVideoFile);
            setImages(imageFiles);
            const combinedMedia = folder?.isSupplySubfolder
              ? [...imageFiles, ...videoFiles]
              : imageFiles;
            setMediaItems(combinedMedia);

            if (combinedMedia.length > 0 && !folder?.isSupplySubfolder) {
              try {
                const mimeType = await ensureThumbnailMimeType();

                const thumbnailTargets = [...imageFiles, ...videoFiles];
                if (thumbnailTargets.length > 0) {
                  setThumbnails({});
                  await getThumbnails(thumbnailTargets, {
                    size: isStoryboards ? "w2048h1536" : "w1024h768",
                    mode: "strict",
                    onBatch: (batchThumbs) => {
                      if (isCancelled) return;
                      const prefixed = {};
                      Object.entries(batchThumbs).forEach(
                        ([pathLower, base64]) => {
                          prefixed[pathLower] = mimeType + base64;
                        }
                      );
                      setThumbnails((prev) => ({ ...prev, ...prefixed }));
                    },
                  });

                  if (isCancelled) return;
                }
              } catch (thumbnailError) {
                console.error(
                  `Failed to load thumbnails for ${folder.name}:`,
                  thumbnailError
                );
              }
            }
          }
        }
      } catch (err) {
        if (isCancelled) return;
        const errorMessage = `Failed to load ${folder.name}: ${err?.message || "Unknown error"
          }`;
        console.error(errorMessage);
        setError(errorMessage);
      } finally {
        if (!isCancelled) {
          setLoading(false);
          onLoadingComplete?.(folder.path, entries.length);
        }
      }
    };

    if (folder?.exists && isActive) {
      loadFolderData();
    } else {
      setLoading(false);
    }

    return () => {
      isCancelled = true;
    };
  }, [
    folder?.path,
    folder?.name,
    folder?.exists,
    isActive,
    isStoryboards,
    folder?.isSupplySubfolder,
    onLoadingComplete,
    ensureThumbnailMimeType,
  ]);

  const handleDownload = useCallback(() => {
    if (folder?.downloadUrl) {
      const downloadUrl = convertToDownloadUrl(folder.downloadUrl);
      window.open(downloadUrl, "_blank");
    }
  }, [folder?.downloadUrl]);

  if (!folder?.exists) return null;

  // Hide Quickproofs folder if it has no files
  if (folder.name === "Quickproofs" && fileCount === 0 && !loading) return null;

  // Remove loading state display for compact folders - they load in background
  if (!isActive && !isCompleted && !compact) {
    return (
      <Card elevation={2}>
        <CardContent>
          <FolderHeader
            name={folder.name}
            downloadUrl={folder.downloadUrl}
            onDownload={handleDownload}
            selectsMade={selectsMade}
            selectedCount={selectedCount}
            onEditSelects={onEditSelects}
            selectsSetup={selectsSetup}
            fileCount={fileCount}
          />
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
              Loading {folder.name}...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const content = isStoryboards ? (
    <>
      <ImageFlexGrid
        loading={loading}
        images={images}
        thumbnails={thumbnails}
        onImageClick={handleMediaOpen}
      />
    </>
  ) : null;

  const wrapperSx = {
    height: "auto",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <>
      <Card
        component={motion.article}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        sx={{
          ...wrapperSx,
          backgroundColor: theme === "dark" ? "#1b1f26" : "#ffffff",
          border: `1px solid ${theme === "dark"
            ? "rgba(255, 255, 255, 0.04)"
            : "rgba(15, 23, 42, 0.08)"
            }`,
          borderRadius: 2,
          boxShadow:
            theme === "dark"
              ? "0 8px 20px rgba(0,0,0,0.45)"
              : "0 10px 20px rgba(15,23,42,0.08)",
        }}
      >
        <CardContent
          sx={{
            maxHeight: isStoryboards ? "600px" : "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: compact ? "center" : "flex-start",
            alignItems: compact ? "stretch" : "stretch",
            "&:last-child": { pb: compact ? 1.5 : 2 },
            p: compact ? 1.5 : 2,
            gap: 1.25,
          }}
        >
          <FolderHeader
            name={folder.name}
            downloadUrl={folder.downloadUrl}
            onDownload={handleDownload}
            compact={compact}
            fileCount={fileCount}
            selectsMade={selectsMade}
            selectedCount={selectedCount}
            onEditSelects={onEditSelects}
            selectsSetup={selectsSetup}
          />

          {folder?.isSupplySubfolder && (
            <MediaPreviewRail
              mediaItems={visibleMediaItems}
              thumbnails={thumbnails}
              onSelect={handleMediaOpen}
              loading={previewRailLoading}
              hasMore={hasMoreMedia}
              onLoadMore={handleLoadMoreMedia}
              onLoadAll={handleLoadAllMedia}
              loadingMore={isLoadingMorePreviews}
            />
          )}
          {error ? (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                background: "rgba(244, 67, 54, 0.1)",
                color: "#ff5722",
                border: "1px solid rgba(244, 67, 54, 0.3)",
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          ) : null}

          {!compact && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                ...(isSmallScreen && { maxHeight: 210 }),
                display: "flex",
                flexDirection: "column",
              }}
            >
              {content}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Lightbox for storyboards and supply previews */}
      {(isStoryboards || folder?.isSupplySubfolder) && (
        <StoryboardLightbox
          isOpen={lightboxOpen}
          onClose={handleLightboxClose}
          mediaItems={mediaItems}
          initialIndex={lightboxIndex}
          thumbnails={thumbnails}
          onRequestMoreMedia={handleLightboxRequestMore}
        />
      )}
    </>
  );
};

/**
 * CollapsibleParentFolder component
 * Shows all subfolder names with file counts and "Load Images" buttons
 */
const SUBFOLDER_COUNT_BATCH_SIZE = 5;
const SUBFOLDER_AUTOLOAD_BATCH_SIZE = SUBFOLDER_COUNT_BATCH_SIZE;

const CollapsibleParentFolder = memo(function CollapsibleParentFolder({
  folder,
  onLoadingComplete,
  selectsMade,
  selectedCount,
  onEditSelects,
  selectsSetup,
}) {
  const [theme] = useAtom(themeAtom);
  const [totalFileCount, setTotalFileCount] = useState(null);
  const [subfolderFileCounts, setSubfolderFileCounts] = useState({});
  const [subfolderStatuses, setSubfolderStatuses] = useState({});
  const [subfolderDownloadUrls, setSubfolderDownloadUrls] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);
  const [autoLoadActivated, setAutoLoadActivated] = useState(false);
  const [parentSharedLink, setParentSharedLink] = useState(null);
  const isDarkMode = theme === "dark";
  const sharedLinkRequestsRef = useRef(new Map());
  const subfolderNamesSignature = useMemo(() => {
    if (!folder?.subfolders) return "";
    return folder.subfolders.map((subfolder) => subfolder.name).join("|");
  }, [folder?.subfolders]);

  useEffect(() => {
    const nextStatuses = {};
    const nextDownloadUrls = {};
    (folder?.subfolders || []).forEach((subfolder) => {
      nextStatuses[subfolder.name] = "pending";
      if (subfolder.downloadUrl) {
        nextDownloadUrls[subfolder.name] = subfolder.downloadUrl;
      }
    });

    setSubfolderStatuses(nextStatuses);
    setSubfolderDownloadUrls(nextDownloadUrls);
    sharedLinkRequestsRef.current.clear();
    setAutoLoadActivated(true);

    // Fetch parent shared link on mount
    const fetchParentLink = async () => {
      if (!folder) return;
      const pathBase = folder.basePathNoMultipleDays;
      const isAbsolute = folder.isAbsolutePath ||
        (folder.path &&
          pathBase &&
          folder.path.toLowerCase().startsWith(pathBase.toLowerCase()));
      const fullPath = isAbsolute ? folder.path : `${pathBase}${folder.path}`;

      try {
        const link = await getOrCreateSharedLink(fullPath);
        if (link) {
          setParentSharedLink(link);
        }
      } catch (error) {
        console.error("Error fetching parent shared link:", error);
      }
    };
    fetchParentLink();
  }, [folder?.basePathNoMultipleDays, subfolderNamesSignature, folder]);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleSubfolderLoadingComplete = useCallback(
    (subfolderName, count) => {
      setSubfolderStatuses((prev) => {
        if (!prev || prev[subfolderName] === "loaded") {
          return prev;
        }
        return { ...prev, [subfolderName]: "loaded" };
      });
      onLoadingComplete?.(subfolderName, count);
    },
    [onLoadingComplete]
  );

  const prepareSharedLink = useCallback(
    async (subfolder) => {
      if (
        !subfolder?.name ||
        !folder?.basePathNoMultipleDays ||
        !subfolder?.path
      ) {
        return null;
      }

      const subfolderName = subfolder.name;

      if (subfolderDownloadUrls[subfolderName]) {
        return subfolderDownloadUrls[subfolderName];
      }

      const existingPromise = sharedLinkRequestsRef.current.get(subfolderName);
      if (existingPromise) {
        return existingPromise;
      }

      const fetchPromise = (async () => {
        const pathBase = folder.basePathNoMultipleDays;
        const isAbsolute = subfolder.isAbsolutePath ||
          (subfolder.path &&
            pathBase &&
            subfolder.path.toLowerCase().startsWith(pathBase.toLowerCase()));

        const fullPath = isAbsolute ? subfolder.path : `${pathBase}${subfolder.path}`;
        console.log("DEBUG: prepareSharedLink", { name: subfolder.name, original: subfolder.path, base: pathBase, isAbs: isAbsolute, full: fullPath });

        try {
          const sharedLink = await getOrCreateSharedLink(fullPath);
          if (sharedLink) {
            setSubfolderDownloadUrls((prev) => {
              if (prev[subfolderName] === sharedLink) {
                return prev;
              }
              return { ...prev, [subfolderName]: sharedLink };
            });
          }
          return sharedLink;
        } catch (error) {
          console.error(`Error preparing shared link for ${subfolder.name}:`, error);
          return null;
        } finally {
          sharedLinkRequestsRef.current.delete(subfolderName);
        }
      })();

      sharedLinkRequestsRef.current.set(subfolderName, fetchPromise);
      return fetchPromise;
    },
    [folder?.basePathNoMultipleDays, subfolderDownloadUrls]
  );

  useEffect(() => {
    if (!folder?.subfolders) return;

    folder.subfolders.forEach((subfolder) => {
      if (subfolderStatuses[subfolder.name] === "loading") {
        prepareSharedLink(subfolder);
      }
    });
  }, [folder?.subfolders, subfolderStatuses, prepareSharedLink]);

  const handlePendingDownloadClick = useCallback(
    async (subfolder) => {
      const link =
        subfolderDownloadUrls[subfolder.name] ||
        (await prepareSharedLink(subfolder));
      if (!link) return;
      const downloadUrl = convertToDownloadUrl(link);
      window.open(downloadUrl, "_blank");
    },
    [subfolderDownloadUrls, prepareSharedLink]
  );

  const handlePendingDropboxClick = useCallback(
    async (subfolder) => {
      const link =
        subfolderDownloadUrls[subfolder.name] ||
        (await prepareSharedLink(subfolder));
      if (!link) return;
      window.open(link, "_blank");
    },
    [subfolderDownloadUrls, prepareSharedLink]
  );

  useEffect(() => {
    if (!autoLoadActivated) return;
    const subfolders = folder?.subfolders || [];
    if (!subfolders.length) return;

    const loadingCount = Object.values(subfolderStatuses).filter(
      (status) => status === "loading"
    ).length;

    const availableSlots = SUBFOLDER_AUTOLOAD_BATCH_SIZE - loadingCount;
    if (availableSlots <= 0) return;

    const updatedStatuses = { ...subfolderStatuses };
    let slots = availableSlots;
    let changed = false;

    for (const subfolder of subfolders) {
      const status = updatedStatuses[subfolder.name];
      if (status === undefined) continue;
      if (status === "pending" && slots > 0) {
        updatedStatuses[subfolder.name] = "loading";
        slots -= 1;
        changed = true;
      }
      if (slots === 0) break;
    }

    if (changed) {
      setSubfolderStatuses(updatedStatuses);
    }
  }, [autoLoadActivated, folder?.subfolders, subfolderStatuses]);

  // Fetch file counts for all subfolders on mount (lightweight operation)
  useEffect(() => {
    let isCancelled = false;

    const fetchAllFileCounts = async () => {
      // Fetch total file count for parent folder
      try {
        const { countFolderContents } = require("../../DropboxFunctions");
        const pathBase = folder.basePathNoMultipleDays;
        const isAbsolute = folder.isAbsolutePath ||
          (folder.path &&
            pathBase &&
            folder.path.toLowerCase().startsWith(pathBase.toLowerCase()));
        const fullPath = isAbsolute ? folder.path : `${pathBase}${folder.path}`;
        console.log("DEBUG: fetchAllFileCounts (Parent)", { name: folder.name, original: folder.path, base: pathBase, isAbs: isAbsolute, full: fullPath });
        const counts = await countFolderContents(fullPath);
        if (!isCancelled) {
          setTotalFileCount(counts.fileCount);
        }
      } catch (error) {
        console.error("Error counting parent folder contents:", error);
        if (!isCancelled) {
          setTotalFileCount(0);
        }
      }

      // Fetch file count for each subfolder
      const { listFolderContents } = require("../../DropboxFunctions");
      setSubfolderFileCounts({});

      const subfolders = folder.subfolders || [];
      for (let i = 0; i < subfolders.length; i += SUBFOLDER_COUNT_BATCH_SIZE) {
        const batch = subfolders.slice(i, i + SUBFOLDER_COUNT_BATCH_SIZE);

        const batchResults = await Promise.all(
          batch.map(async (subfolder) => {
            try {
              const pathBase = folder.basePathNoMultipleDays;
              const isAbsolute = subfolder.isAbsolutePath ||
                (subfolder.path &&
                  pathBase &&
                  subfolder.path.toLowerCase().startsWith(pathBase.toLowerCase()));
              const fullPath = isAbsolute ? subfolder.path : `${pathBase}${subfolder.path}`;
              console.log("DEBUG: fetchAllFileCounts (Sub)", { name: subfolder.name, original: subfolder.path, base: pathBase, isAbs: isAbsolute, full: fullPath });
              const result = await listFolderContents(fullPath);
              return {
                name: subfolder.name,
                count: result.entries ? result.entries.length : 0,
              };
            } catch (error) {
              console.error(`Error counting files for ${subfolder.name}:`, error);
              return { name: subfolder.name, count: 0 };
            }
          })
        );

        if (isCancelled) {
          return;
        }

        setSubfolderFileCounts((prev) => {
          const updatedCounts = { ...prev };
          batchResults.forEach(({ name, count }) => {
            updatedCounts[name] = count;
          });
          return updatedCounts;
        });
      }
    };

    fetchAllFileCounts();
    return () => {
      isCancelled = true;
    };
  }, [folder]);

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      {/* Collapsible Parent Folder Header */}
      <Card
        sx={{
          backgroundColor: isDarkMode ? "#1b1f26" : "#ffffff",
          border: `1px solid ${isDarkMode
            ? "rgba(255, 255, 255, 0.04)"
            : "rgba(15, 23, 42, 0.08)"
            }`,
          borderRadius: 2,
          boxShadow: isDarkMode
            ? "0 8px 20px rgba(0,0,0,0.45)"
            : "0 10px 20px rgba(15,23,42,0.08)",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: isDarkMode
              ? "0 10px 25px rgba(0,0,0,0.55)"
              : "0 12px 24px rgba(15,23,42,0.12)",
          },
        }}
        onClick={handleToggleExpand}
      >
        <CardContent sx={{ py: 2, px: 3, "&:last-child": { pb: 2 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: isDarkMode ? "#f8fafc" : "#1f2434",
                  mb: 0.5,
                }}
              >
                {folder.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode
                    ? "rgba(248, 250, 252, 0.75)"
                    : "rgba(31, 36, 48, 0.7)",
                }}
              >
                {folder.subfolderCount} folders
                {totalFileCount !== null && ` - ${totalFileCount} files`}
              </Typography>
            </Box>

            {/* Download and Dropbox Buttons */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="contained"
                component="a"
                href={parentSharedLink ? convertToDownloadUrl(parentSharedLink) : undefined}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!parentSharedLink}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1,
                  px: 2,
                  py: 0.65,
                  fontSize: "0.85rem",
                  backgroundColor: "#66bb6a",
                  color: "#fff",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#4caf50",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(102, 187, 106, 0.5)",
                    color: "rgba(255, 255, 255, 0.7)"
                  }
                }}
              >
                <Download fontSize="small" sx={{ mr: 0.75 }} />
                Download
              </Button>

              <Button
                variant="contained"
                component="a"
                href={parentSharedLink || undefined}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!parentSharedLink}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1,
                  px: 2,
                  py: 0.65,
                  fontSize: "0.85rem",
                  backgroundColor: "#0061ff",
                  color: "#fff",
                  boxShadow: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  "&:hover": {
                    backgroundColor: "#0051d5",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(0, 97, 255, 0.5)",
                    color: "rgba(255, 255, 255, 0.7)"
                  }
                }}
              >
                <img
                  src="/Dropbox_Icon.png"
                  alt="Dropbox"
                  style={{ width: "16px", height: "16px" }}
                />
                Dropbox
              </Button>

              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? "#90caf9" : "#1976d2",
                  fontWeight: 500,
                  ml: 1,
                }}
              >
                {isExpanded ? "▲" : "▼"}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* List of All Subfolders */}
      <Box sx={{ mt: 2, pl: 3, display: isExpanded ? "block" : "none" }}>
        {(folder.subfolders || []).map((subfolder, index) => {
          const fileCount = subfolderFileCounts[subfolder.name];
          const sharedLinkOverride = subfolderDownloadUrls[subfolder.name];
          const basePathNoMultipleDays = folder.basePathNoMultipleDays || "";
          const pathBase = basePathNoMultipleDays;
          const isAbsolute = subfolder.isAbsolutePath ||
            (subfolder.path &&
              pathBase &&
              subfolder.path.toLowerCase().startsWith(pathBase.toLowerCase()));
          const fullSubfolderPath = isAbsolute ? subfolder.path : `${pathBase}${subfolder.path}`;
          console.log("DEBUG: Render Loop", { name: subfolder.name, original: subfolder.path, base: pathBase, isAbs: isAbsolute, full: fullSubfolderPath });
          const status = subfolderStatuses[subfolder.name] || "pending";
          const isActiveSubfolder = status === "loading" || status === "loaded";

          // Check for separator
          const prevSubfolder = index > 0 ? folder.subfolders[index - 1] : null;
          const showSeparator =
            prevSubfolder &&
            subfolder.parentFolder &&
            prevSubfolder.parentFolder &&
            subfolder.parentFolder !== prevSubfolder.parentFolder;

          const content = isActiveSubfolder ? (
            <Box sx={{ mb: 2 }}>
              <FolderCollection
                folder={{
                  ...subfolder,
                  path: fullSubfolderPath,
                  downloadUrl: sharedLinkOverride || subfolder.downloadUrl || null,
                  exists: true,
                  isSupplySubfolder: true,
                  parentFolder: subfolder.parentFolder || null,
                  subfolderName: subfolder.subfolderName || null,
                }}
                isActive={true}
                isCompleted={status === "loaded"}
                onLoadingComplete={handleSubfolderLoadingComplete}
                compact={true}
                selectsMade={selectsMade}
                selectedCount={selectedCount}
                onEditSelects={onEditSelects}
                selectsSetup={selectsSetup}
              />
            </Box>
          ) : (
            <Card
              sx={{
                backgroundColor: isDarkMode ? "#1b1f26" : "#ffffff",
                border: `1px solid ${isDarkMode
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(15, 23, 42, 0.08)"
                  }`,
                borderRadius: 2,
                boxShadow: isDarkMode
                  ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                  : "0 4px 12px rgba(15, 23, 42, 0.06)",
                mb: 1.5,
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: isDarkMode ? "#f8fafc" : "#1f2434",
                        mb: 0.25,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {subfolder.name}
                      <CircularProgress
                        size={16}
                        sx={{
                          color: isDarkMode ? "#90caf9" : "#1976d2",
                        }}
                      />
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8rem",
                        color: isDarkMode
                          ? "rgba(248, 250, 252, 0.6)"
                          : "rgba(31, 36, 48, 0.6)",
                      }}
                    >
                      {fileCount !== undefined
                        ? `${fileCount} files`
                        : "Loading..."}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.25,
                      alignItems: "center",
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePendingDownloadClick(subfolder);
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 1,
                        px: 2,
                        py: 0.65,
                        fontSize: "0.85rem",
                        backgroundColor: "#66bb6a",
                        color: "#fff",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#4caf50",
                        },
                      }}
                    >
                      <Download fontSize="small" sx={{ mr: 0.75 }} />
                      Download
                    </Button>

                    <Button
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePendingDropboxClick(subfolder);
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 1,
                        px: 2,
                        py: 0.65,
                        fontSize: "0.85rem",
                        backgroundColor: "#0061ff",
                        color: "#fff",
                        boxShadow: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        "&:hover": {
                          backgroundColor: "#0051d5",
                        },
                      }}
                    >
                      <img
                        src="/Dropbox_Icon.png"
                        alt="Dropbox"
                        style={{ width: "16px", height: "16px" }}
                      />
                      Dropbox
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );

          return (
            <React.Fragment key={subfolder.name || index}>
              {showSeparator && (
                <Box
                  sx={{
                    width: "100%",
                    height: "1px",
                    backgroundColor: isDarkMode ? "#333" : "#e0e0e0",
                    my: 2,
                  }}
                />
              )}
              {content}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
});

export default FolderCollection;
export { CollapsibleParentFolder };
